import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type {
  TemplateDryRunResult,
  TemplateApplyResult,
  TemplatePreflightResult,
  TemplatePreflightIssue,
  PendingConflictsSnapshot,
} from '@volley/crucible-ipc-types/ipc-channels';
import { unflattenIpcError } from '@volley/crucible-ipc-types/error-types';
import { usePendingConflicts, useResolveWithAi } from '../hooks/useConflictResolution';
import { useCrucibleClient } from '../transport/context';
import { projectQueryKey } from '../transport/project-query-key';

type Phase =
  | 'loading-preflight'
  | 'preflight-blocked'
  | 'pulling-latest'
  | 'stopping-dev-server'
  | 'loading-preview'
  | 'preview'
  | 'breaking-gate'
  | 'applying'
  | 'done'
  /**
   * Issue #613: `template:apply` rejected with `CRUCIBLE-312`
   * (Pending conflict resolution). This is NOT a fatal error — the
   * merge produced sidecar markers that the Resolve-with-AI flow
   * (PR #600) is designed to surface. Distinct phase so the body
   * renders affordances (Resolve with AI / Open Workspace / CLI
   * hint) instead of the red "Update failed" banner.
   */
  | 'conflict-resolution'
  | 'error';

const BREAKING_CONFIRM_TEXT = 'I understand';

/**
 * Modal that orchestrates the full template-update flow:
 *
 *   loading-preview → preview → [breaking-gate] → applying → done
 *
 * The breaking-change gate is a typed confirmation ("I understand"). The
 * core apply step runs typecheck and shipped skill tests before it commits
 * the update. A verification failure rejects the apply and rolls source files
 * plus the lockfile back inside the core update transaction.
 *
 * Issue #391: when `template:apply` returns a non-empty `conflicts` list,
 * the Done body offers an in-modal AskUserQuestion-style picker — let the
 * agent walk the user through each conflict per the existing
 * `update-template` SKILL §5 contract, or close and resolve manually
 * (current behaviour). The host (Workspace.tsx) wires
 * `onResolveWithAgent` to dispatch a synthesised chat message; when the
 * prop is absent (e.g. Dashboard, where there's no chat panel) the modal
 * falls back to the manual-resolve message.
 */
export function UpdateTemplateModal({
  gameId,
  open,
  onClose,
  onResolveWithAgent,
  onOpenWorkspace,
}: {
  gameId: string;
  open: boolean;
  onClose: () => void;
  /**
   * Issue #391: invoked when the user picks "Resolve with the agent"
   * on the conflict-resolution picker. Receives the conflicted-file
   * list verbatim from `applyResult.conflicts`. The host is expected
   * to synthesise a chat-pane prompt, dispatch it through the chat
   * reducer, and have the agent follow the existing `update-template`
   * SKILL §5 contract (read each file, propose a resolution,
   * explain what was kept vs adopted).
   *
   * When undefined the modal hides the agent option entirely and
   * shows the legacy "resolve them manually" copy.
   */
  onResolveWithAgent?: (conflictedFiles: string[]) => void;
  /**
   * Issue #613: invoked when the user picks "Open Workspace" or
   * "Resolve with AI" on the CRUCIBLE-312 conflict-resolution
   * branch. The host (Dashboard) wires this to a router navigate
   * call so the user lands on the game's Workspace where the
   * existing `<ConflictResolutionCard>` is also visible (belt-and-
   * braces). When undefined the buttons render but only close the
   * modal — useful on hosts that already are the Workspace.
   */
  onOpenWorkspace?: () => void;
}): React.JSX.Element | null {
  const client = useCrucibleClient();
  const queryClient = useQueryClient();
  const [phase, setPhase] = useState<Phase>('loading-preflight');
  const [preflight, setPreflight] = useState<TemplatePreflightResult | null>(null);
  const [dryRun, setDryRun] = useState<TemplateDryRunResult | null>(null);
  const [applyResult, setApplyResult] = useState<TemplateApplyResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [verificationRolledBack, setVerificationRolledBack] = useState(false);
  /**
   * Macroscope round 1 catch: when the user clicked "Stop dev
   * server and continue" and that failed, the body rendered "Pull
   * failed: <dev:stop error>". Track which recovery op produced the
   * inline error so the label matches the operation. Cleared
   * whenever a fresh attempt starts so a stale label can't outlive
   * its message.
   */
  const [errorLabel, setErrorLabel] = useState<string | null>(null);
  const [breakingConfirm, setBreakingConfirm] = useState('');
  const [conflictStrategy, setConflictStrategy] = useState<'ours' | 'theirs' | undefined>(
    undefined,
  );

  /**
   * Issue #156: gate the update behind a preflight check before
   * loading the dry-run. If the working tree is dirty, the local
   * branch is detached, or there's no upstream, the apply path is
   * blocked outright. If the only blocking issue is `behind`, the
   * user gets a single "Pull latest and continue" CTA — see
   * `handlePullLatest` below.
   */
  const runPreflightAndContinue = async (): Promise<void> => {
    try {
      const result = await client.invoke('template:preflight', { gameId });
      setPreflight(result);
      // Route to preflight-blocked when EITHER there's a hard
      // block (dirty / behind / detached / agent-in-flight) OR
      // when a non-blocking warning needs explicit acknowledgement
      // (dev-server-running). Issue #241: surfacing the warning
      // gate is the whole point of the dev-server check; we
      // can't drop straight into the preview when Vite is still
      // hot-reloading.
      const needsAck = result.issues.some((i) => i.blocking || i.kind === 'dev-server-running');
      if (needsAck) {
        setPhase('preflight-blocked');
        return;
      }
      setPhase('loading-preview');
      const dry = await client.invoke('template:dry-run', { gameId });
      setDryRun(dry);
      setPhase('preview');
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : String(err));
      setPhase('error');
    }
  };

  // Reset state and kick off preflight on open.
  useEffect(() => {
    if (!open) return;
    setPhase('loading-preflight');
    setPreflight(null);
    setDryRun(null);
    setApplyResult(null);
    setErrorMessage(null);
    setVerificationRolledBack(false);
    setErrorLabel(null);
    setBreakingConfirm('');
    setConflictStrategy(undefined);
    void runPreflightAndContinue();
    // `runPreflightAndContinue` closes over `gameId` only; safe to
    // exclude from deps so the effect doesn't re-fire on every
    // identity change of the closure.
  }, [open, gameId]);

  /**
   * Auto-fix companion to the `behind`-only block. Runs `git pull
   * --ff-only` on the game's working tree, then re-runs the
   * preflight to confirm we're clean. If the pull itself fails
   * (diverged history → not fast-forwardable), surface the reason
   * back on the preflight-blocked screen so the user can resolve
   * by hand.
   */
  /**
   * Issue #241 auto-fix companion to the `dev-server-running`
   * warning. Calls `dev:stop` for the session id the preflight
   * result handed us, then re-runs preflight to confirm the
   * server's gone. If `dev:stop` itself rejects (orchestrator
   * wedged, process refusing to die) we surface the reason on the
   * blocked screen and leave the user to resolve manually.
   */
  const handleStopDevServer = async (): Promise<void> => {
    if (!preflight?.devSessionId) {
      // Defensive — the footer only shows this CTA when
      // devSessionId is present, but if the preflight result
      // was massaged in flight we'd otherwise call dev:stop
      // with undefined and crash.
      return;
    }
    setPhase('stopping-dev-server');
    setErrorMessage(null);
    setErrorLabel(null);
    try {
      // Codex review #4 round-6 MEDIUM — pass gameId so
      // main can acquire the per-game launch lock BEFORE
      // doing the sessionId lookup, closing the map-empty
      // race with an in-flight preview:restart.
      await client.invoke('dev:stop', { sessionId: preflight.devSessionId, gameId });
      // Re-run preflight to confirm the server's gone (and to
      // pick up any changes the user made while we were
      // gating). The fresh result drives the next phase
      // transition.
      await runPreflightAndContinue();
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : String(err));
      setErrorLabel('Stop dev server failed');
      setPhase('preflight-blocked');
    }
  };

  const handlePullLatest = async (): Promise<void> => {
    setPhase('pulling-latest');
    setErrorMessage(null);
    setErrorLabel(null);
    try {
      const pullResult = await client.invoke('template:pull-latest', { gameId });
      if (!pullResult.pulled) {
        // Re-fetch preflight so the renderer reflects the
        // post-pull state (ahead/behind counts may have
        // shifted even on a failed FF). Show the failure
        // reason inline on the blocked screen.
        const recheck = await client.invoke('template:preflight', { gameId });
        setPreflight(recheck);
        setErrorMessage(pullResult.reason ?? 'Pull failed');
        setErrorLabel('Pull failed');
        setPhase('preflight-blocked');
        return;
      }
      // Pull succeeded — re-run preflight to confirm clean,
      // then load the preview.
      await runPreflightAndContinue();
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : String(err));
      setErrorLabel('Pull failed');
      setPhase('error');
    }
  };

  if (!open) return null;

  const beginApply = async (force: boolean): Promise<void> => {
    setPhase('applying');
    setErrorMessage(null);
    setVerificationRolledBack(false);
    try {
      const result = await client.invoke('template:apply', {
        gameId,
        force,
        ...(conflictStrategy && { conflictStrategy }),
      });
      setApplyResult(result);
      setPhase('done');
      // Bust the update-check cache so the badge/banner recompute.
      void queryClient.invalidateQueries({
        queryKey: projectQueryKey(client, ['template:check-updates', gameId]),
      });
    } catch (err) {
      // Issue #613: discriminate the pending-conflict-resolution
      // terminal state from a genuine fatal error. The producer
      // side (packages/crucible/src/core/update.ts) throws
      // CRUCIBLE-312 after writing `.foundry/pending-conflicts
      // .json`; the sidecar IS the recovery surface, so the modal
      // must offer the inline Resolve-with-AI / Open Workspace
      // affordances rather than the red "Update failed" banner.
      // Decoding via `unflattenIpcError` peels off Electron's
      // "Error invoking remote method '…': CrucibleError: <json>"
      // wrapper so we read the structured `code` field, not the
      // wrapped message string.
      const decoded = unflattenIpcError(err);
      if (decoded.code === 'CRUCIBLE-312') {
        setPhase('conflict-resolution');
        return;
      }
      if (decoded.code === 'CRUCIBLE-316') {
        setVerificationRolledBack(true);
        setErrorMessage(decoded.message);
        setPhase('error');
        return;
      }
      setErrorMessage(err instanceof Error ? err.message : String(err));
      setPhase('error');
    }
  };

  const handleUpdate = (): void => {
    if (!dryRun) return;
    if (dryRun.isBreaking) {
      setPhase('breaking-gate');
    } else {
      void beginApply(false);
    }
  };

  const handleBreakingConfirm = (): void => {
    if (breakingConfirm.trim() === BREAKING_CONFIRM_TEXT) {
      void beginApply(true);
    }
  };

  return (
    <div
      // Codex review #4 MEDIUM (PR #646 / #643) — bumped from
      // z-50 to z-[60] so this update-template modal beats
      // the preview-recovery toast (z-[55]). See
      // DependencyApprovalModal for the full rationale.
      //
      // `app-region-no-drag` mirrors the PreviewOverlay pattern
      // (PR #847): the modal launches from the Dashboard, which
      // is now an `app-region-drag` header (this PR). Without
      // the opt-out, a backdrop click landing over the header
      // strip on macOS is interpreted by the OS compositor as
      // a window-drag rather than the click-to-dismiss the
      // `onClick={onClose}` handler expects.
      className="app-region-no-drag fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl rounded-lg border border-white/10 bg-crucible-surface shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-3">
          <h2 className="text-base font-semibold text-white/90">Update template</h2>
          <button
            onClick={onClose}
            disabled={
              phase === 'applying' || phase === 'stopping-dev-server' || phase === 'pulling-latest'
            }
            className="text-sm text-white/40 hover:text-white/70 disabled:opacity-40"
          >
            {phase === 'done' || phase === 'conflict-resolution' ? 'Close' : 'Cancel'}
          </button>
        </div>

        {/* Body */}
        <div className="p-5 text-sm text-white/80">
          {phase === 'loading-preflight' && (
            <div className="flex items-center gap-3">
              <Spinner />
              <span className="text-white/60">Checking your working tree…</span>
            </div>
          )}

          {phase === 'preflight-blocked' && preflight && (
            <PreflightBlockedBody
              preflight={preflight}
              errorMessage={errorMessage}
              {...(errorLabel != null && { errorLabel })}
              mode={resolveBlockedMode(preflight)}
            />
          )}

          {phase === 'pulling-latest' && (
            <div className="flex items-center gap-3">
              <Spinner />
              <span>Pulling latest from upstream…</span>
            </div>
          )}

          {phase === 'stopping-dev-server' && (
            <div className="flex items-center gap-3">
              <Spinner />
              <span>Stopping dev server…</span>
            </div>
          )}

          {phase === 'loading-preview' && (
            <p className="text-white/60">Checking what will change…</p>
          )}

          {phase === 'preview' && dryRun && (
            <PreviewBody
              dryRun={dryRun}
              conflictStrategy={conflictStrategy}
              setConflictStrategy={setConflictStrategy}
            />
          )}

          {phase === 'breaking-gate' && dryRun && (
            <BreakingGateBody
              dryRun={dryRun}
              confirmText={breakingConfirm}
              setConfirmText={setBreakingConfirm}
            />
          )}

          {phase === 'applying' && (
            <div className="flex items-center gap-3">
              <Spinner />
              <span>Applying update…</span>
            </div>
          )}

          {phase === 'done' && applyResult && (
            <DoneBody
              applyResult={applyResult}
              onResolveManually={onClose}
              {...(onResolveWithAgent && {
                onResolveWithAgent: (conflicts: string[]) => {
                  onResolveWithAgent(conflicts);
                  onClose();
                },
              })}
            />
          )}

          {phase === 'conflict-resolution' && (
            <ConflictResolutionBody
              gameId={gameId}
              onClose={onClose}
              {...(onOpenWorkspace && { onOpenWorkspace })}
            />
          )}

          {phase === 'error' && (
            <div className="rounded-md border border-red-500/30 bg-red-900/20 p-3 text-red-200">
              <p className="mb-1 font-medium">
                {verificationRolledBack ? 'Checks failed; update rolled back' : 'Update failed'}
              </p>
              {verificationRolledBack ? (
                <pre className="max-h-40 overflow-auto whitespace-pre-wrap text-xs text-red-300/90">
                  {errorMessage}
                </pre>
              ) : (
                <p className="text-xs text-red-300/90">{errorMessage}</p>
              )}
              {verificationRolledBack && (
                <p className="mt-2 text-xs text-red-300/90">
                  Source files and the lockfile were restored. Dependencies installed in{' '}
                  <code>node_modules</code> may remain until the next pnpm install.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-white/10 px-5 py-3">
          {phase === 'preflight-blocked' && preflight && (
            <PreflightBlockedFooter
              preflight={preflight}
              onCancel={onClose}
              onPullLatest={() => void handlePullLatest()}
              onStopDevServer={() => void handleStopDevServer()}
            />
          )}

          {phase === 'preview' && dryRun && (
            <>
              <button
                onClick={onClose}
                className="rounded-md px-3 py-1.5 text-sm text-white/60 hover:text-white/80"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                className={`rounded-md px-4 py-1.5 text-sm font-medium text-white hover:opacity-90 ${
                  dryRun.isBreaking ? 'bg-amber-600' : 'bg-sky-600'
                }`}
              >
                {dryRun.isBreaking ? 'Continue…' : `Update to v${dryRun.target}`}
              </button>
            </>
          )}

          {phase === 'breaking-gate' && (
            <>
              <button
                onClick={() => setPhase('preview')}
                className="rounded-md px-3 py-1.5 text-sm text-white/60 hover:text-white/80"
              >
                Back
              </button>
              <button
                onClick={handleBreakingConfirm}
                disabled={breakingConfirm.trim() !== BREAKING_CONFIRM_TEXT}
                className="rounded-md bg-amber-600 px-4 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-40"
              >
                Apply breaking update
              </button>
            </>
          )}

          {phase === 'done' && (
            <button
              onClick={onClose}
              className="rounded-md bg-sky-600 px-4 py-1.5 text-sm font-medium text-white hover:opacity-90"
            >
              Done
            </button>
          )}

          {phase === 'conflict-resolution' && (
            <button
              onClick={onClose}
              className="rounded-md bg-white/10 px-4 py-1.5 text-sm font-medium text-white hover:bg-white/20"
            >
              Close
            </button>
          )}

          {phase === 'error' && (
            <button
              onClick={onClose}
              className="rounded-md bg-white/10 px-4 py-1.5 text-sm font-medium text-white hover:bg-white/20"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function PreviewBody({
  dryRun,
  conflictStrategy,
  setConflictStrategy,
}: {
  dryRun: TemplateDryRunResult;
  conflictStrategy: 'ours' | 'theirs' | undefined;
  setConflictStrategy: (s: 'ours' | 'theirs' | undefined) => void;
}): React.JSX.Element {
  return (
    <div className="space-y-4">
      <div className="flex items-baseline gap-3">
        <span className="text-white/50">Template</span>
        <span className="font-mono text-white/90">v{dryRun.current}</span>
        <span className="text-white/50">→</span>
        <span className="font-mono text-white/90">v{dryRun.target}</span>
        {dryRun.isBreaking && (
          <span className="rounded bg-amber-500/30 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-200">
            Breaking
          </span>
        )}
      </div>

      {dryRun.releaseNotes && (
        <div>
          <p className="mb-1 text-xs font-medium text-white/70">What&apos;s in this update</p>
          <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded-md border border-white/10 bg-black/20 px-3 py-2 font-mono text-[11px] leading-relaxed text-white/80">
            {dryRun.releaseNotes}
          </pre>
        </div>
      )}

      {dryRun.updated.length > 0 && (
        <FileList title="Will update" files={dryRun.updated} tone="text-sky-300" />
      )}
      {dryRun.added.length > 0 && (
        <FileList title="Will add" files={dryRun.added} tone="text-green-300" />
      )}
      {dryRun.warnings.length > 0 && (
        <FileList title="Notes" files={dryRun.warnings} tone="text-amber-300" />
      )}

      <details className="text-xs text-white/50">
        <summary className="cursor-pointer">Advanced: conflict resolution</summary>
        <div className="mt-2 space-y-2">
          <p>
            If the 3-way merge hits a conflict, pick whose version wins. Default is to write
            conflict markers into the file and stop.
          </p>
          <div className="flex gap-3">
            <label className="flex items-center gap-1.5">
              <input
                type="radio"
                name="conflict"
                checked={conflictStrategy === undefined}
                onChange={() => setConflictStrategy(undefined)}
              />
              Stop on conflict
            </label>
            <label className="flex items-center gap-1.5">
              <input
                type="radio"
                name="conflict"
                checked={conflictStrategy === 'ours'}
                onChange={() => setConflictStrategy('ours')}
              />
              Keep my edits
            </label>
            <label className="flex items-center gap-1.5">
              <input
                type="radio"
                name="conflict"
                checked={conflictStrategy === 'theirs'}
                onChange={() => setConflictStrategy('theirs')}
              />
              Take template
            </label>
          </div>
        </div>
      </details>
    </div>
  );
}

function BreakingGateBody({
  dryRun,
  confirmText,
  setConfirmText,
}: {
  dryRun: TemplateDryRunResult;
  confirmText: string;
  setConfirmText: (s: string) => void;
}): React.JSX.Element {
  return (
    <div className="space-y-3">
      <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-amber-100">
        <p className="mb-2 font-medium">This is a breaking update</p>
        <p className="text-xs text-amber-200/90">
          Going from v{dryRun.current} to v{dryRun.target} means the template changed something that
          your game may need to migrate. You might see type errors or runtime failures after the
          update. Read the template release notes before proceeding. If you hit issues you can't
          fix, <code className="rounded bg-black/30 px-1">git revert HEAD</code> will roll back to
          your pre-update state.
        </p>
      </div>
      <label className="block">
        <span className="text-xs text-white/60">
          Type <code className="rounded bg-white/10 px-1">{BREAKING_CONFIRM_TEXT}</code> to
          continue:
        </span>
        <input
          type="text"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          autoFocus
          className="mt-1 w-full rounded-md border border-white/10 bg-black/20 px-3 py-1.5 text-sm text-white/90 focus:border-amber-500/50 focus:outline-none"
          placeholder={BREAKING_CONFIRM_TEXT}
        />
      </label>
    </div>
  );
}

/**
 * Issue #391: synthesise the chat prompt the user would have typed if
 * they'd asked the agent to resolve template-update conflicts by hand.
 * Pure function so the wording can be pinned by a unit test (the user
 * reviews the exact phrasing on every PR that touches it). The agent
 * already has the `update-template` SKILL prompt loaded and follows the
 * existing §5 contract — read each file, propose a resolution, explain
 * what was kept vs adopted — same output the CLI path produces today.
 */
export function synthesiseConflictResolutionPrompt(conflictedFiles: readonly string[]): string {
  const fileList = conflictedFiles.join(', ');
  return (
    `The template update left conflict markers in ${fileList}. ` +
    `Please read the SKILL.md file for /update-template and resolve each conflict — ` +
    `explain what you kept and what you adopted.`
  );
}

/* @internal — exported for SSR tests only; not part of the public API. */
export function DoneBody({
  applyResult,
  onResolveWithAgent,
  onResolveManually,
}: {
  applyResult: TemplateApplyResult;
  /**
   * Issue #391: when defined AND `applyResult.conflicts.length > 0`,
   * the body renders the AskUserQuestion-style picker (agent vs
   * manual). When undefined, falls back to the legacy "resolve them
   * manually" copy. The wrapping closure in the parent already calls
   * `onClose()` after dispatching, so this callback is fire-and-
   * forget from the body's perspective.
   */
  onResolveWithAgent?: (conflictedFiles: string[]) => void;
  /**
   * Closes the modal when the user picks "I'll resolve them myself"
   * on the conflict picker. The parent maps this to `onClose` so the
   * picker dismisses the same way the footer's Done button does.
   */
  onResolveManually?: () => void;
}): React.JSX.Element {
  const hasConflicts = applyResult.conflicts.length > 0;
  return (
    <div className="space-y-3">
      <div className="rounded-md border border-green-500/30 bg-green-900/20 p-3 text-green-200">
        <p className="font-medium">Updated to template v{applyResult.templateVersion}.</p>
        {hasConflicts && !onResolveWithAgent && (
          <p className="mt-1 text-xs text-amber-300">
            {applyResult.conflicts.length} file(s) have merge conflicts — resolve them manually.
          </p>
        )}
      </div>

      {hasConflicts && onResolveWithAgent && (
        <ConflictResolutionPicker
          conflicts={applyResult.conflicts}
          onResolveWithAgent={onResolveWithAgent}
          {...(onResolveManually && { onResolveManually })}
        />
      )}

      {/*
        crucible#3492: the dry-run preview already rendered these as
        "Notes" (see PreviewBody above) — but a REAL apply's warnings
        (ingot-catalogue deltas, hw#2082/#2086 gated-self-test
        incompatibilities, kept-but-differs removed files) were
        silently dropped here, so anything discovered only during the
        real apply (not previewed) never reached the user at all.
      */}
      {applyResult.warnings.length > 0 && (
        <FileList title="Notes" files={applyResult.warnings} tone="text-amber-300" />
      )}

      <p className="text-xs text-white/50">
        What changed is logged in{' '}
        <code className="rounded bg-white/10 px-1">docs/template-updates.md</code>.
      </p>
    </div>
  );
}

/**
 * Issue #391: AskUserQuestion-style picker shown after a `template:apply`
 * that left merge conflicts. Two options:
 *
 *   1. **Resolve with the agent (recommended)** — invokes
 *      `onResolveWithAgent(conflicts)`, which the host wires to a chat
 *      dispatch carrying the prompt synthesised by
 *      `synthesiseConflictResolutionPrompt`. The agent reads the
 *      `update-template` SKILL §5 contract and walks each conflict.
 *   2. **I'll resolve them myself** — closes the modal; user is on their
 *      own with the conflict markers. Matches pre-#391 behaviour.
 *
 * The component does NOT close the modal directly on the agent path —
 * the parent's `onResolveWithAgent` wrapper handles `onClose()` AFTER
 * the dispatch fires, so a host that wants to leave the modal open
 * (e.g. for follow-up state) can do so by NOT calling `onClose()` in
 * its wrapper.
 */
/* @internal — exported for SSR tests only; not part of the public API. */
export function ConflictResolutionPicker({
  conflicts,
  onResolveWithAgent,
  onResolveManually,
}: {
  conflicts: readonly string[];
  onResolveWithAgent: (conflictedFiles: string[]) => void;
  /**
   * Optional override for the manual-resolve path. When omitted, the
   * button is a no-op visually (the modal's outer Cancel/Close still
   * dismisses it). Defaults to undefined so the live modal can rely
   * on its existing footer buttons; the prop exists for SSR tests
   * that render this picker outside the modal shell.
   */
  onResolveManually?: () => void;
}): React.JSX.Element {
  return (
    <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3">
      <p className="mb-1 text-sm font-medium text-amber-200">
        {conflicts.length} file(s) have merge conflicts after the update.
      </p>
      <p className="mb-2 text-xs text-amber-200/80">
        Want the agent to resolve them? It will read each file and propose a fix per the{' '}
        <code className="rounded bg-black/30 px-1">/update-template</code> contract — you review and
        accept or push back in Build.
      </p>
      <ul className="mb-3 max-h-24 overflow-auto rounded-md border border-amber-500/20 bg-black/20 px-2 py-1 font-mono text-[11px] text-amber-200/90">
        {conflicts.map((f) => (
          <li key={f}>{f}</li>
        ))}
      </ul>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onResolveWithAgent([...conflicts])}
          className="rounded-md bg-sky-600 px-3 py-1.5 text-xs font-medium text-white hover:opacity-90"
        >
          Resolve with the agent (recommended)
        </button>
        <button
          type="button"
          onClick={() => onResolveManually?.()}
          className="rounded-md border border-white/10 px-3 py-1.5 text-xs text-white/70 hover:bg-white/5"
        >
          I&apos;ll resolve them myself
        </button>
      </div>
    </div>
  );
}

/**
 * Issue #613 — body for the `conflict-resolution` phase. Mounted when
 * `template:apply` rejects with a CrucibleError whose `code` is
 * `CRUCIBLE-312` (Pending conflict resolution). The producer side
 * (`packages/crucible/src/core/update.ts`) wrote `.foundry/pending-
 * conflicts.json` before throwing, so we read the sidecar via the
 * same `usePendingConflicts` hook the Workspace's
 * `<ConflictResolutionCard>` uses — that gives us the version-from /
 * -to + the conflicted-file list with zero new IPC surface.
 *
 * Affordances:
 *
 *   - **Resolve with AI** — fires `conflicts:resolve-with-ai` (same
 *     channel the Workspace card fires) with the sidecar's file list,
 *     then closes the modal and navigates to the game's Workspace so
 *     the user sees the agent's progress in the chat panel.
 *
 *   - **Open Workspace** — closes the modal and navigates to the
 *     Workspace where the existing card is also visible. Belt-and-
 *     braces for users who'd rather pick the path manually.
 *
 *   - **Copy CLI command** — `crucible update --accept-theirs
 *     <gameId>` on the clipboard for the user who wants to bypass
 *     the renderer entirely.
 *
 * Renders `role="status"` (NOT `role="alert"`) — this isn't a fatal
 * error, it's a routing signpost. The amber/sky tone mirrors the
 * existing dev-server-warning surface so the visual hierarchy
 * matches the semantic ("informational, not refused").
 *
 * `usePendingConflicts` polls the sidecar; if the file is briefly
 * absent (race against main's PostToolBatch cleanup) we render the
 * best-effort copy without the file list, so the affordances still
 * work and the user isn't stranded.
 */
function ConflictResolutionBody({
  gameId,
  onClose,
  onOpenWorkspace,
}: {
  gameId: string;
  onClose: () => void;
  onOpenWorkspace?: () => void;
}): React.JSX.Element {
  // Hook calls live here (not in `ConflictResolutionBranch`) so the
  // SSR test path that imports `ConflictResolutionBranch` directly
  // can pass `sidecarOverride` without standing up a QueryClient.
  const live = usePendingConflicts(gameId);
  const { resolveWithAi } = useResolveWithAi();
  return (
    <ConflictResolutionBranch
      gameId={gameId}
      sidecar={live.data ?? null}
      resolveWithAi={resolveWithAi}
      onClose={onClose}
      {...(onOpenWorkspace && { onOpenWorkspace })}
    />
  );
}

/**
 * SSR-friendly inner component for the conflict-resolution branch.
 * Splits the hook plumbing (`ConflictResolutionBody`) from the pure-
 * markup branch so the SSR test suite can render the affordances
 * without standing up a QueryClient or stubbing IPC. The
 * `resolveWithAi` dependency is injected so the test can pin both
 * the success and failure branches.
 */
/* @internal — exported for SSR tests only; not part of the public API. */
export function ConflictResolutionBranch({
  gameId,
  sidecar,
  resolveWithAi,
  onClose,
  onOpenWorkspace,
}: {
  gameId: string;
  sidecar: PendingConflictsSnapshot | null;
  resolveWithAi: (args: {
    gameId: string;
    files: string[];
  }) => Promise<{ ok: boolean; error?: string }>;
  onClose: () => void;
  onOpenWorkspace?: () => void;
}): React.JSX.Element {
  const [pending, setPending] = useState(false);
  const [dispatchError, setDispatchError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const fileCount = sidecar?.files.length ?? 0;
  const cliCommand = `crucible update --accept-theirs ${gameId}`;

  const handleResolveWithAi = async (): Promise<void> => {
    if (!sidecar || sidecar.files.length === 0) {
      // Sidecar hasn't been polled yet (or vanished mid-render).
      // Fall back to navigating — the Workspace card will pick
      // up the sidecar via its own poll and the user can fire
      // the IPC from there.
      onClose();
      onOpenWorkspace?.();
      return;
    }
    setPending(true);
    setDispatchError(null);
    try {
      const files = sidecar.files.map((f) => f.path);
      const result = await resolveWithAi({ gameId, files });
      if (!result.ok) {
        setDispatchError(
          result.error ??
            'Could not dispatch the agent. Open the Workspace and try again from there.',
        );
        setPending(false);
        return;
      }
      // Success — close the modal and navigate to the Workspace
      // so the user sees the agent's resolution stream in chat.
      onClose();
      onOpenWorkspace?.();
    } catch (err) {
      const decoded = unflattenIpcError(err);
      setDispatchError(
        decoded.message ||
          'Something went wrong dispatching the agent. Try again from the Workspace.',
      );
      setPending(false);
    }
  };

  const handleOpenWorkspace = (): void => {
    onClose();
    onOpenWorkspace?.();
  };

  const handleCopyCli = (): void => {
    void navigator.clipboard
      .writeText(cliCommand)
      .then(() => {
        setCopied(true);
        // Reset the "Copied" affordance after a short beat so
        // the user can copy again without reloading.
        window.setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {
        // Clipboard rejected (permissions, headless, etc.) —
        // leave the label as "Copy" so the user can retry.
        setCopied(false);
      });
  };

  const versionLine = sidecar
    ? `The template update from v${sidecar.templateFrom} → v${sidecar.templateTo} has unresolved differences in ${fileCount} file${fileCount === 1 ? '' : 's'}.`
    : 'The template update has unresolved differences. Open the Workspace to see the affected files and pick a resolution.';

  return (
    <div
      data-testid="update-modal-conflict-resolution"
      role="status"
      aria-live="polite"
      className="space-y-3"
    >
      <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3">
        <p className="mb-1 text-sm font-medium text-amber-200">Conflicts pending</p>
        <p className="text-xs text-amber-200/80">{versionLine}</p>
      </div>

      {sidecar && sidecar.files.length > 0 && (
        <ul
          data-testid="update-modal-conflict-file-list"
          className="max-h-32 overflow-auto rounded-md border border-amber-500/20 bg-black/20 px-2 py-1 font-mono text-[11px] text-amber-200/90"
        >
          {sidecar.files.map((f) => (
            <li key={f.path}>
              <span className="text-amber-100">{f.path}</span>{' '}
              <span className="text-amber-200/60">
                ({f.conflictBlocks} {f.conflictBlocks === 1 ? 'conflict block' : 'blocks'})
              </span>
            </li>
          ))}
        </ul>
      )}

      {dispatchError && (
        <div
          data-testid="update-modal-conflict-dispatch-error"
          className="rounded-md border border-red-500/30 bg-red-900/20 p-2 text-xs text-red-200"
        >
          {dispatchError}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          data-testid="update-modal-resolve-with-ai"
          onClick={() => {
            void handleResolveWithAi();
          }}
          disabled={pending}
          className="rounded-md bg-sky-600 px-3 py-1.5 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50"
        >
          {pending ? 'Dispatching…' : 'Resolve with AI'}
        </button>
        <button
          type="button"
          data-testid="update-modal-open-workspace"
          onClick={handleOpenWorkspace}
          disabled={pending}
          className="rounded-md border border-white/15 px-3 py-1.5 text-xs text-white/80 hover:bg-white/5 disabled:opacity-50"
        >
          Open Workspace
        </button>
      </div>

      <div className="rounded-md border border-white/10 bg-black/20 p-3 text-xs text-white/70">
        <p className="mb-1 text-white/50">Or resolve from the CLI:</p>
        <div className="flex items-center gap-2">
          <code
            data-testid="update-modal-cli-hint"
            className="flex-1 overflow-x-auto whitespace-nowrap rounded bg-black/40 px-2 py-1 font-mono text-[11px] text-white/80"
          >
            {cliCommand}
          </code>
          <button
            type="button"
            data-testid="update-modal-copy-cli"
            onClick={handleCopyCli}
            className="rounded-md border border-white/15 px-2 py-1 text-[11px] text-white/70 hover:bg-white/5"
          >
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      </div>
    </div>
  );
}

function FileList({
  title,
  files,
  tone,
}: {
  title: string;
  files: string[];
  tone: string;
}): React.JSX.Element {
  return (
    <div>
      <p className={`mb-1 text-xs font-medium ${tone}`}>
        {title} ({files.length})
      </p>
      <ul className="max-h-32 overflow-auto rounded-md border border-white/5 bg-black/20 px-2 py-1.5 font-mono text-[11px] text-white/70">
        {files.map((f) => (
          <li key={f}>{f}</li>
        ))}
      </ul>
    </div>
  );
}

function Spinner(): React.JSX.Element {
  return (
    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/20 border-t-sky-400" />
  );
}

/**
 * Two reasons the modal can be on the `preflight-blocked` phase
 * (Issue #241 added the warn-dev-server mode). Each drives a distinct
 * banner tone + footer CTA combination. Computed once per render
 * from the issue set so body and footer stay in sync.
 */
export type BlockedMode =
  /** At least one hard-blocking issue (dirty / behind / detached / agent-in-flight). */
  | 'blocked'
  /** Only the dev-server warning is present — non-blocking but needs ack. */
  | 'warn-dev-server';

/**
 * Pick the dominant gate-mode for a preflight result. Hard blocks
 * win over warnings, so a dev-server-running + dirty combo lands in
 * `blocked` mode (the user sees the hard-stop UI).
 */
export function resolveBlockedMode(preflight: TemplatePreflightResult): BlockedMode {
  const hasHardBlock = preflight.issues.some((i) => i.blocking);
  if (hasHardBlock) return 'blocked';
  const hasDevWarning = preflight.issues.some((i) => i.kind === 'dev-server-running');
  if (hasDevWarning) return 'warn-dev-server';
  return 'blocked';
}

/**
 * Body for the `preflight-blocked` phase. Lists each blocking issue
 * with its kind-specific icon + message and exposes the recovery path
 * appropriate for the dominant blocker (Pull when only-`behind`, Stop
 * dev server when only-`dev-server-running`, manual when anything
 * else). The CTA itself lives in the footer; this body is the
 * reason-code surface the user reads to understand why they're
 * blocked.
 */
/* @internal — exported for SSR tests only; not part of the public API. */
export function PreflightBlockedBody({
  preflight,
  errorMessage,
  errorLabel,
  mode,
}: {
  preflight: TemplatePreflightResult;
  /**
   * Human-readable failure message from the most recent recovery
   * attempt (Pull or Stop dev server). The accompanying `errorLabel`
   * names the operation so the user reads "Stop dev server failed: …"
   * rather than the misleading "Pull failed: …" the v1 of this prop
   * always rendered (Macroscope round 1).
   */
  errorMessage?: string | null;
  /**
   * Operation that produced `errorMessage`, used as the prefix in
   * the inline failure banner. Required when `errorMessage` is
   * non-null so the user always sees a coherent label.
   */
  errorLabel?: string;
  mode: BlockedMode;
}): React.JSX.Element {
  // Show every issue (blocking + non-blocking warnings). Sorting:
  // blocking first so the user reads the hard-stop reasons before
  // the warnings, matching the order of the footer CTAs.
  const visible = [
    ...preflight.issues.filter((i) => i.blocking),
    ...preflight.issues.filter((i) => !i.blocking),
  ];
  const isWarnOnly = mode === 'warn-dev-server';
  // Codex round 2 P3 catch (#251): when the ONLY blocker is the
  // agent, the hard-block banner used to describe a "dirty or
  // out-of-date branch" — pointing the user at git fixes that
  // wouldn't help. Use generic "active work" copy when no git
  // issue is present. The per-row message still spells out the
  // exact recovery (click Stop in the chat).
  const blockingIssues = preflight.issues.filter((i) => i.blocking);
  // Codex round 3 P3: include `no-upstream` in the git-blocker set
  // even though today it's emitted as a non-blocking issue. Belt-
  // and-braces against a future change that flips it to blocking —
  // we want any git kind to keep the git rationale copy, never
  // fall through to the agent copy.
  const hasGitBlocker = blockingIssues.some(
    (i) =>
      i.kind === 'dirty' ||
      i.kind === 'behind' ||
      i.kind === 'detached' ||
      i.kind === 'no-upstream',
  );
  const blockedRationale = hasGitBlocker
    ? 'The template apply step rewrites tracked files. Updating from a dirty or out-of-date branch produces merge tangles that are much harder to unpick after the fact than refusing up front.'
    : "The template apply step rewrites tracked files. Updating while the agent is still working would let two writers stomp on the same files, so we refuse until the agent's turn finishes.";
  // Macroscope round 1 catch: distinct tone per mode. Hard-block
  // banner is amber (matches the existing dirty / behind UI from
  // PR #173). Warning-only banner is sky to telegraph
  // "informational, not refused" — matches the per-row tone for
  // non-blocking issues so the visual hierarchy doesn't lie.
  return (
    <div className="space-y-3">
      <div
        className={
          isWarnOnly
            ? 'rounded-md border border-sky-500/30 bg-sky-500/5 p-3'
            : 'rounded-md border border-amber-500/30 bg-amber-500/5 p-3'
        }
      >
        <p
          className={
            isWarnOnly
              ? 'mb-1 text-sm font-medium text-sky-200'
              : 'mb-1 text-sm font-medium text-amber-200'
          }
        >
          {isWarnOnly ? 'Heads up before you update' : "Can't update yet"}
        </p>
        <p className={isWarnOnly ? 'text-xs text-sky-200/80' : 'text-xs text-amber-200/80'}>
          {isWarnOnly
            ? 'The template apply step rewrites tracked files. Stopping the dev server first avoids hot-reloading a half-applied state.'
            : blockedRationale}
        </p>
      </div>

      <ul className="space-y-2">
        {visible.map((issue, idx) => (
          <PreflightIssueRow key={`${issue.kind}-${idx}`} issue={issue} />
        ))}
      </ul>

      <div className="rounded-md border border-white/10 bg-black/20 px-3 py-2 text-xs text-white/60">
        <span className="text-white/40">Branch:</span>{' '}
        <span className="font-mono text-white/80">{preflight.branch}</span>
        {preflight.hasUpstream && (
          <>
            <span className="ml-3 text-white/40">Ahead:</span>{' '}
            <span className="font-mono text-white/80">{preflight.ahead}</span>
            <span className="ml-3 text-white/40">Behind:</span>{' '}
            <span className="font-mono text-white/80">{preflight.behind}</span>
          </>
        )}
      </div>

      {errorMessage && (
        <div className="rounded-md border border-red-500/30 bg-red-900/20 p-2 text-xs text-red-200">
          <span className="font-medium">{errorLabel ?? 'Failed'}: </span>
          {errorMessage}
        </div>
      )}
    </div>
  );
}

function PreflightIssueRow({ issue }: { issue: TemplatePreflightIssue }): React.JSX.Element {
  // Icon is purely decorative — kept as a single character so screen
  // readers don't trip on it. Issue #241 added the agent + dev
  // entries; pin them in the test so a future tweak ("oh, stick a
  // 🔨 here") doesn't quietly alter the surface.
  const icon =
    issue.kind === 'dirty'
      ? '✎'
      : issue.kind === 'behind'
        ? '⇣'
        : issue.kind === 'no-upstream'
          ? '∅'
          : issue.kind === 'detached'
            ? '⊘'
            : issue.kind === 'agent-in-flight'
              ? '⏳'
              : issue.kind === 'dev-server-running'
                ? '⏵'
                : '⊘';
  // Hard-block issues use the existing amber tone; the dev-server
  // warning sits on a quieter sky tone so the user can tell at a
  // glance "this is informational, not refused".
  const tone = issue.blocking ? 'text-amber-300/80' : 'text-sky-300/80';
  return (
    <li className="flex items-start gap-2 text-sm">
      <span className={`mt-0.5 ${tone}`} aria-hidden="true">
        {icon}
      </span>
      <span className="text-white/80">{issue.message}</span>
    </li>
  );
}

/**
 * Footer for the `preflight-blocked` phase. Three CTA shapes depending
 * on the issue set:
 *
 *   1. Hard-block, no auto-fix (dirty / detached / agent-in-flight) →
 *      Cancel only. Force is too risky for any of these.
 *   2. Hard-block, every blocker is canAutoFix (currently only-`behind`) →
 *      Cancel + "Pull latest and continue".
 *   3. Warning-only, dev server running → Cancel + "Stop dev server
 *      and continue". The renderer pulls `preflight.devSessionId` to
 *      drive `dev:stop` directly without a second lookup.
 *
 * If `behind` AND `dev-server-running` are both present, the dominant
 * mode is `blocked` (hard wins), so the dev-server CTA is suppressed
 * in favour of Pull. The user can re-trigger the update after
 * pulling, and the next preflight pass will surface the dev warning
 * fresh.
 */
/* @internal — exported for SSR tests only; not part of the public API. */
export function PreflightBlockedFooter({
  preflight,
  onCancel,
  onPullLatest,
  onStopDevServer,
}: {
  preflight: TemplatePreflightResult;
  onCancel: () => void;
  onPullLatest: () => void;
  onStopDevServer: () => void;
}): React.JSX.Element {
  const blocking = preflight.issues.filter((i) => i.blocking);
  const allBlockingAutoFixable = blocking.length > 0 && blocking.every((i) => i.canAutoFix);
  const onlyDevWarning =
    blocking.length === 0 && preflight.issues.some((i) => i.kind === 'dev-server-running');
  return (
    <>
      <button
        onClick={onCancel}
        className="rounded-md px-3 py-1.5 text-sm text-white/60 hover:text-white/80"
      >
        Cancel
      </button>
      {allBlockingAutoFixable && (
        <button
          onClick={onPullLatest}
          className="rounded-md bg-sky-600 px-4 py-1.5 text-sm font-medium text-white hover:opacity-90"
        >
          Pull latest and continue
        </button>
      )}
      {onlyDevWarning && preflight.devSessionId && (
        <button
          onClick={onStopDevServer}
          className="rounded-md bg-sky-600 px-4 py-1.5 text-sm font-medium text-white hover:opacity-90"
        >
          Stop dev server and continue
        </button>
      )}
    </>
  );
}
